import React, { ChangeEvent, useState, useRef } from "react";
import { Lock, Trash2, UploadCloud, File, Download, Loader2, Eye } from "lucide-react";
import { error, success } from "../../../utils/toast";
import apiMethods from "../../../utils/api";
import { api } from "../../hooks/api";
import PageLoader from "../LoadingPage";
import { color } from "../../styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "axios";
import { getStoredAuthToken } from "../../../utils/authToken";
import { getFileTypeIcon } from "../../../widgets/FileList/utils/fileTypeIcon";

dayjs.extend(utc);
dayjs.extend(timezone);

interface FileUploadProps {
    model: string; // e.g., "product_flow.SaleOrder", "materials.MaterialOrder", "products.Customer"
    objectId: string;
    objectName?: string; // Display name for the linked object (e.g., "DBH_25_011")
    customerName?: string; // Customer name if applicable
    customerId?: string; // Customer ID for hyperlink
    maxFileSize?: number; // Maximum file size in bytes (default: 25MB)
    disabled?: boolean; // Whether upload is disabled
    className?: string; // Additional CSS classes
}

const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const FileUpload = ({ 
    model, 
    objectId, 
    objectName, 
    customerName, 
    customerId,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    disabled = false,
    className = ""
}: FileUploadProps) => {
    const hasTarget = Boolean(model && objectId && !disabled);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Build list URL dynamically
    const getListUrl = () => {
        if (hasTarget) {
            return `/files/?model=${model}&object_id=${objectId}`;
        }
        return "/files/";
    };

    const [{ data, isLoading }, fetchFiles] = api.get(getListUrl(), undefined, !hasTarget);

    if (isLoading && hasTarget) return <PageLoader />;

    const files = hasTarget ? data?.results || [] : [];

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatUploadTime = (dateString: string): string => {
        if (!dateString) return '';
        // Convert to Vietnam timezone (Asia/Ho_Chi_Minh)
        const vietnamTime = dayjs(dateString).tz('Asia/Ho_Chi_Minh');
        const day = vietnamTime.date().toString().padStart(2, '0');
        const month = (vietnamTime.month() + 1).toString().padStart(2, '0');
        const year = vietnamTime.year();
        const hours = vietnamTime.hour().toString().padStart(2, '0');
        const minutes = vietnamTime.minute().toString().padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const handlePreview = async (file: any) => {
        if (!file.is_previewable || !file.preview_url) {
            // Fallback to download if not previewable
            handleDownload(file);
            return;
        }
        
        try {
            // Fetch file with authentication headers
            // preview_url is already a full absolute URL from backend
            const previewUrl = file.preview_url;
            const token = getStoredAuthToken();
            
            // Fetch file blob with authentication
            const response = await axios.get(previewUrl, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : undefined,
                },
                responseType: 'blob',
            });
            
            // Create blob URL and open in new tab
            const blob = new Blob([response.data], { type: file.mime_type || response.headers['content-type'] });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            
            // Clean up blob URL after a delay (browser will handle it when tab closes)
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (err: any) {
            console.error("Preview error:", err);
            error("Không thể mở xem trước tệp tin", err?.response?.data?.detail || err?.message || "");
        }
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        if (file.size > maxFileSize) {
            const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
            error(`Kích thước tệp tin không được vượt quá ${maxSizeMB}MB`);
            return;
        }

        if (!model || !objectId) {
            error("Không thể xác định đối tượng đính kèm");
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // Step 1: Initiate upload
            setUploadProgress(10);
            const initiateResponse: any = await apiMethods.post("/files/initiate/", {
                model,
                object_id: objectId,
                filename: file.name,
                size_bytes: file.size,
                mime_type: file.type,
                is_public: false,
            });

            const { file_id, put_sas_url, required_headers } = initiateResponse;
            
            console.log("Upload details:", { file_id, put_sas_url, required_headers });

            // Step 2: Upload to Azure
            setUploadProgress(30);
            const azureResponse = await fetch(put_sas_url, {
                method: "PUT",
                headers: required_headers,
                body: file,
            });
            
            console.log("Azure response:", azureResponse.status, azureResponse.statusText);

            if (!azureResponse.ok) {
                const errorText = await azureResponse.text();
                console.error("Azure upload failed:", errorText);
                throw new Error(`Upload to Azure failed: ${azureResponse.status} - ${errorText}`);
            }

            // Step 3: Calculate checksum for integrity verification
            setUploadProgress(60);
            const arrayBuffer = await file.arrayBuffer();
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const checksum = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            // Step 4: Complete upload with checksum
            setUploadProgress(80);
            await apiMethods.post("/files/complete/", {
                file_id,
                checksum,
            });

            // Step 5: Link file to object with metadata
            setUploadProgress(90);
            const meta: any = {};
            if (objectName) meta.object_name = objectName;
            if (customerName) meta.customer_name = customerName;
            if (customerId) meta.customer_id = customerId;
            
            await apiMethods.post(`/files/${file_id}/link/`, {
                model,
                object_id: objectId,
                meta: Object.keys(meta).length > 0 ? meta : undefined,
            });

            setUploadProgress(100);
            await fetchFiles();
            success("THÀNH CÔNG", "Tải tệp tin thành công");
            
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (err: any) {
            console.error("Upload error:", err);
            error("Tải lên tệp tin thất bại", err?.message || "");
        } finally {
            setUploading(false);
            setTimeout(() => setUploadProgress(0), 500);
        }
    };

    const handleDeleteFile = async (fileId: string) => {
        if (deletingFileId) return;
        setDeletingFileId(fileId);
        try {
            await apiMethods.delete(`/files/${fileId}/`);
            await fetchFiles();
            success("THÀNH CÔNG", "Xóa tệp tin thành công");
        } catch (err) {
            error("Xóa tệp tin thất bại");
        } finally {
            setDeletingFileId(null);
        }
    };

    const handleDownload = async (file: any) => {
        try {
            const downloadUrl = `/files/${file.id}/download/`;
            
            // Fetch to get the SAS URL
            const response: any = await apiMethods.get(downloadUrl);
            
            if (response.download_url) {
                // Open SAS URL directly
                window.open(response.download_url, '_blank');
                success("THÀNH CÔNG", "Đang tải xuống tệp tin...");
            } else {
                throw new Error('No download URL returned');
            }
        } catch (err) {
            error("Tải xuống tệp tin thất bại");
        }
    };

    const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));

    return (
        <div className={`tw-rounded-lg tw-bg-white tw-border tw-w-full tw-overflow-hidden ${className}`}
             style={{ border: `1px solid ${color.secondary.BORDER}`, boxSizing: 'border-box' }}>
            {/* Header */}
            <div className="tw-px-4 sm:tw-px-5 tw-pt-4 tw-pb-3 tw-w-full tw-overflow-hidden" style={{ boxSizing: 'border-box' }}>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3 tw-min-w-0 tw-w-full">
                    <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-truncate tw-min-w-0">Tệp đính kèm</h3>
                    {files.length > 0 && (
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[24px] tw-h-6 tw-rounded-full tw-bg-blue-50 tw-px-2 tw-text-xs tw-font-medium tw-shrink-0 tw-ml-2"
                              style={{ color: color.status.INFORMATION }}>
                            {files.length}
                        </span>
                    )}
                </div>

                {/* Upload Button */}
                <div className="tw-w-full tw-overflow-hidden" style={{ boxSizing: 'border-box' }}>
                    <label
                        className={`tw-relative tw-flex tw-items-center tw-gap-2 tw-w-full tw-min-w-0 tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-dashed tw-transition-all tw-duration-200 ${
                            hasTarget 
                                ? "tw-border-blue-300 tw-bg-blue-50/30 hover:tw-border-blue-400 hover:tw-bg-blue-50/50 tw-cursor-pointer" 
                                : "tw-border-gray-200 tw-bg-gray-50 tw-cursor-not-allowed"
                        } ${uploading ? "tw-opacity-60 tw-pointer-events-none" : ""}`}
                        style={{ boxSizing: 'border-box', width: '100%' }}
                        aria-disabled={!hasTarget}
                        title={hasTarget ? "Tải lên tệp" : "Lưu đơn hàng để bật tải tệp"}
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin tw-shrink-0" style={{ color: color.status.INFORMATION }} />
                                <span className="tw-text-xs tw-font-medium tw-truncate tw-min-w-0 tw-flex-1" style={{ color: color.status.INFORMATION }}>
                                    Đang tải lên... {uploadProgress}%
                                </span>
                            </>
                        ) : (
                            <>
                                <UploadCloud className={`tw-h-4 tw-w-4 tw-shrink-0 ${hasTarget ? "" : "tw-text-gray-400"}`} 
                                            style={hasTarget ? { color: color.status.INFORMATION } : {}} />
                                <span className={`tw-text-xs tw-font-medium tw-truncate tw-min-w-0 tw-flex-1 ${hasTarget ? "" : "tw-text-gray-400"}`}
                                      style={hasTarget ? { color: color.status.INFORMATION } : {}}>
                                    {hasTarget ? "Chọn tệp để tải lên" : "Bị khóa"}
                                </span>
                            </>
                        )}
                        <input 
                            ref={fileInputRef}
                            type="file" 
                            onChange={handleFileChange} 
                            className="tw-hidden" 
                            disabled={!hasTarget || uploading} 
                        />
                    </label>
                </div>

                {/* File upload info */}
                {hasTarget && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
                        Hỗ trợ tất cả các loại tệp (PDF, Excel, Word, hình ảnh, v.v.) • Tối đa {maxSizeMB}MB
                    </p>
                )}

                {/* Upload Progress Bar */}
                {uploading && uploadProgress > 0 && (
                    <div className="tw-mt-2.5 tw-w-full tw-h-1 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
                        <div 
                            className="tw-h-full tw-transition-all tw-duration-300 tw-ease-out tw-rounded-full tw-shadow-sm"
                            style={{ 
                                width: `${uploadProgress}%`,
                                background: `linear-gradient(90deg, ${color.status.INFORMATION} 0%, ${color.primary.BLUE_B200} 100%)`
                            }}
                        />
                    </div>
                )}
            </div>

            {/* File List */}
            {hasTarget && files.length > 0 ? (
                <div className="tw-px-4 sm:tw-px-5 tw-pb-4 tw-space-y-1 tw-w-full tw-overflow-hidden" style={{ boxSizing: 'border-box' }}>
                    {files.map((file: any) => (
                        <div
                            key={file.id}
                            className="tw-group/file-item tw-flex tw-items-center tw-justify-between tw-gap-2 tw-py-1.5 tw-px-1 tw-rounded-md tw-transition-all tw-duration-200"
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = `0 0 8px ${color.status.INFORMATION}40, 0 0 12px ${color.status.INFORMATION}30`;
                                e.currentTarget.style.border = `1px solid ${color.status.INFORMATION}`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '';
                                e.currentTarget.style.border = '';
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    if (file.is_previewable && file.preview_url) {
                                        handlePreview(file);
                                    } else {
                                        handleDownload(file);
                                    }
                                }}
                                className="tw-flex tw-flex-1 tw-min-w-0 tw-items-center tw-gap-2 tw-text-left tw-transition-all tw-duration-200 tw-border-0 tw-bg-transparent tw-p-0"
                                title={file.filename_original}
                            >
                                <div className="tw-shrink-0 group-hover/file-item:tw-drop-shadow-[0_0_4px_rgba(32,150,217,0.7)] tw-transition-all tw-duration-200">
                                    {getFileTypeIcon(file.filename_original || '', file.mime_type)}
                                </div>
                                <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
                                    <span 
                                        className="tw-text-sm tw-truncate tw-transition-all tw-duration-200"
                                        style={{ 
                                            color: color.status.INFORMATION,
                                        }}
                                    >
                                        {file.filename_original}
                                    </span>
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500">
                                        {file.size_bytes && (
                                            <span className="tw-shrink-0">
                                                {formatFileSize(file.size_bytes)}
                                            </span>
                                        )}
                                        {file.created_at && (
                                            <>
                                                {file.size_bytes && <span>•</span>}
                                                <span className="tw-shrink-0">
                                                    {formatUploadTime(file.created_at)}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </button>
                            <div className="tw-flex tw-items-center tw-gap-1">
                                {/* Preview Icon - shows on hover if previewable */}
                                {file.is_previewable && file.preview_url && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePreview(file);
                                        }}
                                        className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-text-gray-700 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-all tw-duration-150 tw-shrink-0 tw-border-0 tw-bg-transparent tw-opacity-0 group-hover/file-item:tw-opacity-100"
                                        aria-label="Xem trước"
                                        title="Xem trước"
                                    >
                                        <Eye className="tw-h-5 tw-w-5" />
                                    </button>
                                )}
                                {/* Download Icon - shows on hover */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(file);
                                    }}
                                    className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-text-gray-700 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-all tw-duration-150 tw-shrink-0 tw-border-0 tw-bg-transparent tw-opacity-0 group-hover/file-item:tw-opacity-100"
                                    aria-label="Tải xuống"
                                    title="Tải xuống"
                                >
                                    <Download className="tw-h-5 tw-w-5" />
                                </button>
                                {/* Delete Icon */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFile(file.id);
                                    }}
                                    disabled={deletingFileId !== null}
                                    className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-text-gray-700 hover:tw-text-red-600 hover:tw-bg-red-50 tw-transition-all tw-duration-150 tw-shrink-0 tw-border-0 tw-bg-transparent tw-opacity-0 group-hover/file-item:tw-opacity-100"
                                    aria-label="Xóa tệp"
                                    title="Xóa tệp"
                                >
                                    {deletingFileId === file.id ? (
                                        <Loader2 className="tw-h-5 tw-w-5 tw-animate-spin" />
                                    ) : (
                                        <Trash2 className="tw-h-5 tw-w-5" />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : hasTarget ? (
                <div className="tw-px-4 sm:tw-px-5 tw-pb-4 tw-w-full tw-overflow-hidden" style={{ boxSizing: 'border-box' }}>
                    <p className="tw-text-sm tw-text-gray-500 tw-py-2">Chưa có tệp đính kèm</p>
                </div>
            ) : (
                <div className="tw-px-4 sm:tw-px-5 tw-pb-4 tw-w-full tw-overflow-hidden" style={{ boxSizing: 'border-box' }}>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-py-2">
                        <Lock className="tw-h-4 tw-w-4 tw-text-gray-400 tw-shrink-0" />
                        <p className="tw-text-sm tw-text-gray-500">
                            Lưu đơn hàng để bật tải tệp
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;
